import { GradientStop, GradientConfig, StarLayerConfig } from './StarFieldRenderer';

/**
 * Load configurations from JSON file
 * @param {string} configPath - Path to the configuration file
 * @returns {Promise<Object>} - The loaded configuration or null if loading fails
 */
export async function loadConfiguration(configPath) {
  try {
    const response = await fetch(configPath);
    if (!response.ok) {
      throw new Error(`Failed to load configuration: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error loading configuration:', error);
    return null;
  }
}

/**
 * Convert JSON configuration to proper class instances
 * @param {Object} config - Raw configuration from JSON
 * @returns {Object} - Configuration with proper class instances
 */
export function parseConfiguration(config) {
  if (!config) return null;
  
  let result = {};
  
  // Parse gradient configuration
  if (config.gradient) {
    const stops = config.gradient.stops.map(stop => 
      new GradientStop(stop.color, stop.position)
    );
    
    result.gradientConfig = new GradientConfig({
      direction: config.gradient.direction,
      stops: stops
    });
  }
  
  // Parse star layer configurations
  if (config.starLayers) {
    result.starLayerConfigs = config.starLayers.map(layer => {
      return new StarLayerConfig({
        colorBase: layer.colorBase,
        colorRandom: layer.colorRandom,
        sizeBase: layer.sizeBase,
        sizeRandom: layer.sizeRandom,
        starCount: layer.starCount,
        rotationPeriod: layer.rotationPeriod
      });
    });
  }
  
  // Parse global settings
  if (config.settings) {
    result.settings = config.settings;
  }
  
  return result;
}

/**
 * Create default configurations
 * @returns {Object} - Default configurations
 */
export function getDefaultConfigurations() {
  // Default star layer configs
  const starLayerConfigs = [
    new StarLayerConfig({
      colorBase: { r: 0.8, g: 0.8, b: 1.0, a: 1.0 },
      colorRandom: { r: 0.2, g: 0.2, b: 0.0, a: 0.2 },
      sizeBase: 1.5,
      sizeRandom: 2.5,
      starCount: 2000,
      rotationPeriod: 12500
    }),
    new StarLayerConfig({
      colorBase: { r: 0.9, g: 0.9, b: 0.6, a: 0.9 },
      colorRandom: { r: 0.1, g: 0.1, b: 0.2, a: 0.1 },
      sizeBase: 1.2,
      sizeRandom: 1.8,
      starCount: 1500,
      rotationPeriod: 20000
    }),
    new StarLayerConfig({
      colorBase: { r: 0.9, g: 0.5, b: 0.5, a: 0.8 },
      colorRandom: { r: 0.1, g: 0.3, b: 0.2, a: 0.2 },
      sizeBase: 1.0,
      sizeRandom: 1.2,
      starCount: 1000,
      rotationPeriod: 50000
    })
  ];
  
  // Default gradient config
  const gradientConfig = new GradientConfig();
  
  // Default settings
  const settings = {
    antiAliasing: true
  };
  
  return { starLayerConfigs, gradientConfig, settings };
}
